import { FieldAnswer, FieldWithAnswer } from "src/api/types.generated";
import { snakeToCamelCase } from "src/shared/utils/string";
import { Nullable } from "src/shared/utils/typescript";

import { DiscoveryProfile, TeammateRelationship } from "./types";

type ProfileField = Pick<FieldWithAnswer, "slug"> & {
  answer?: Nullable<Pick<FieldAnswer, "value">>;
};

export const mapDiscoveryProfile = (
  id: number,
  fields: ProfileField[]
): DiscoveryProfile =>
  fields.reduce<Partial<DiscoveryProfile>>(
    (profileData, field) => {
      if (!field.slug) {
        return profileData;
      }

      const value = field.answer?.value;
      return {
        ...profileData,
        [snakeToCamelCase(field.slug) as keyof DiscoveryProfile]:
          field.slug === "profile_photo" ? value?.[0] : value,
      };
    },
    { id }
  ) as DiscoveryProfile;

const parseClaimIdString = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const id = Number(trimmed);
  return id > 0 ? id : null;
};

export interface GetTeammateRelationshipArgs {
  authenticatedClaimId: Nullable<number>;
  authenticatedTeammateClaimIds: Nullable<readonly string[]>;
  targetClaimId: number;
  targetTeammateClaimIds: Nullable<readonly string[]>;
}

/**
 * Derive the authenticated hacker's relationship to a directory profile from
 * both sides' teammate claim ID lists. Does not introduce team entities.
 */
export const getTeammateRelationship = ({
  authenticatedClaimId,
  authenticatedTeammateClaimIds,
  targetClaimId,
  targetTeammateClaimIds,
}: GetTeammateRelationshipArgs): TeammateRelationship => {
  if (
    authenticatedClaimId == null ||
    !Number.isInteger(authenticatedClaimId) ||
    authenticatedClaimId <= 0 ||
    authenticatedTeammateClaimIds == null ||
    !Number.isInteger(targetClaimId) ||
    targetClaimId <= 0 ||
    targetClaimId === authenticatedClaimId
  ) {
    return "none";
  }

  // Incoming-only (target lists auth, auth does not) is none.
  if (
    !authenticatedTeammateClaimIds.some(
      (id) => parseClaimIdString(id) === targetClaimId
    )
  ) {
    return "none";
  }

  return (targetTeammateClaimIds ?? []).some(
    (id) => parseClaimIdString(id) === authenticatedClaimId
  )
    ? "mutual"
    : "pending";
};
